import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield, RefreshCw, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OTPModal({ phone, name, onVerified, onClose }) {
  const { t } = useLang();
  const [generatedOTP, setGeneratedOTP] = useState(null);
  const [enteredOTP, setEnteredOTP] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    requestOTP();
  }, []);

  const requestOTP = async () => {
    setLoading(true);
    setError("");
    setEnteredOTP("");
    try {
      const res = await axios.post(`${API}/auth/customer/request-otp`, { phone });
      setGeneratedOTP(res.data.otp);
    } catch {
      setError("Failed to generate OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (enteredOTP.length < 6) { setError("Enter the 6-digit OTP"); return; }
    setVerifying(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/customer/verify-otp`, {
        phone,
        otp: enteredOTP,
        name: name || undefined
      });
      onVerified(res.data.phone, res.data.name, res.data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid OTP. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border text-sm text-center tracking-[0.4em] font-bold text-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D97A46]/30 transition-colors";
  const inputStyle = { backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', color: 'var(--c-text)' };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        data-testid="otp-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-sm rounded-2xl shadow-2xl border"
          style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}
          data-testid="otp-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--c-border)' }}>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" style={{ color: '#D97A46' }} />
              <h2 className="font-serif text-lg font-bold" style={{ color: 'var(--c-text)' }}>{t('otp.title')}</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-full transition-colors hover:opacity-70" style={{ color: 'var(--c-muted)' }}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-5">
            {/* Phone info */}
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('otp.verifying')}</p>
              <p className="font-semibold text-lg mt-0.5" style={{ color: 'var(--c-text)' }}>+91 {phone}</p>
            </div>

            {loading ? (
              <div className="flex flex-col items-center py-6 gap-3">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D97A46', borderTopColor: 'transparent' }} />
                <p className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('otp.generating')}</p>
              </div>
            ) : (
              <>
                {/* OTP Display Box */}
                {generatedOTP && (
                  <div className="p-4 rounded-2xl border-2 text-center" style={{ borderColor: '#D97A46', backgroundColor: 'rgba(217,122,70,0.05)' }}
                    data-testid="otp-display-box">
                    <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#D97A46' }}>
                      {t('otp.yourCode')}
                    </p>
                    <p className="font-mono text-4xl font-bold tracking-[0.3em] select-all" style={{ color: 'var(--c-text)' }}
                      data-testid="otp-code-display">
                      {generatedOTP}
                    </p>
                    <p className="text-xs mt-2" style={{ color: 'var(--c-muted)' }}>{t('otp.validity')}</p>
                  </div>
                )}

                {/* OTP Input */}
                <form onSubmit={handleVerify} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>
                      {t('otp.enterLabel')}
                    </label>
                    <input
                      className={inputCls}
                      style={inputStyle}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={enteredOTP}
                      onChange={e => { setEnteredOTP(e.target.value.replace(/\D/g, "")); setError(""); }}
                      placeholder={t('otp.placeholder')}
                      autoFocus
                      data-testid="otp-input"
                    />
                    {error && <p className="text-xs text-red-500 mt-1.5" data-testid="otp-error">{error}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={verifying || enteredOTP.length < 6}
                    data-testid="verify-otp-btn"
                    className="w-full py-3 rounded-full font-semibold text-white flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ backgroundColor: '#D97A46' }}
                  >
                    {verifying ? (
                      <><div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" /> {t('otp.verifyingBtn')}</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4" /> {t('otp.verify')}</>
                    )}
                  </button>
                </form>

                {/* Resend */}
                <div className="text-center">
                  <button
                    onClick={requestOTP}
                    className="text-xs flex items-center gap-1 mx-auto transition-colors hover:opacity-70"
                    style={{ color: 'var(--c-muted)' }}
                    data-testid="resend-otp-btn"
                  >
                    <RefreshCw className="w-3 h-3" /> {t('otp.resend')}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
